import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useCheckPermission from '../../utils/permissions/useCheckPermissions';
import MaterialTable, {
    MTableFilterRow,
    MTableToolbar,
} from '@material-table/core';
import EventDataService from '../../services/EventService';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import 'date-fns';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import LocationDataService from '../../services/LocationService';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx/dist/xlsx.full.min.js';
import DescriptionIcon from '@material-ui/icons/Description';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import dayjs from 'dayjs';
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers';
import DateTimePickerWithManualInput from '../common/DateTimePickerWithManualInput';
import {
    formatToolbarDateForApi,
    getDefaultToolbarEndDate,
    normalizeToolbarEndDate,
    normalizeToolbarStartDate,
} from '../../utils/reportDateDefaults';
import { useDepartmentsOptions } from '../../utils/useDepartmentsOptions';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/bg';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import ModalForm from './Modal';
import { useTranslation } from 'react-i18next';
import { getAppTimezone, toTzDate } from '../../utils/timezone';
import { getMaterialTableExportData } from '../../utils/getMaterialTableExportData';
import {
    isEventInDateRange,
    sanitizeEventRow,
    sanitizeEventRows,
} from '../../utils/eventRowUtils';
import { useIsMounted } from '../../utils/useIsMounted';
import { reportDatePickerSlotProps } from '../../utils/reportDatePickerSlotProps';
import { getAdminIdFromCookie } from '../../utils/jwtUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

const useStyles = makeStyles(theme => ({
    absoluteContainer: {
        position: 'absolute',
        zIndex: 9,
        width: '45vw',
        [theme.breakpoints.down('md')]: {
            position: 'initial',
        },
    },
    filterButton: {
        marginLeft: theme.spacing(1),
        marginTop: theme.spacing(2.5),
    },
}));

const EventDateTimeEditField = ({ value, onChange, dateConfig, t }) => {
    const editValue = value ? dayjs(value).tz(getAppTimezone()) : null;

    const handleEditDateChange = newValue => {
        const now = dayjs();
        if (newValue && newValue.isAfter(now)) {
            alert(t('reports.common.noFutureDate'));
            return;
        }
        onChange(newValue ? newValue.toISOString() : null);
    };

    return (
        <DateTimePickerWithManualInput
            value={editValue}
            onChange={handleEditDateChange}
            format={dateConfig.format}
            locale={dateConfig.locale}
            disableFuture
            maxDateTime={dayjs()}
            minWidth={250}
            size="small"
            variant="standard"
        />
    );
};

function EventsTable() {
    const { t, i18n } = useTranslation();
    const isMountedRef = useIsMounted();
    const fetchIdRef = useRef(0);
    const tableRef = useRef(null);

    // Function to get date format and locale based on current language
    const getDateFormatAndLocale = () => {
        const language = i18n.language;
        switch (language) {
            case 'en':
                return {
                    format: 'MM/DD/YYYY HH:mm:ss',
                    locale: 'en',
                    dateFormatExport: date => {
                        return `${date.getMonth() +
                            1}/${date.getDate()}/${date.getFullYear()}, ${date
                                .getHours()
                                .toString()
                                .padStart(2, '0')}:${date
                                    .getMinutes()
                                    .toString()
                                    .padStart(2, '0')}:${date
                                        .getSeconds()
                                        .toString()
                                        .padStart(2, '0')}`;
                    },
                };
            case 'es':
                return {
                    format: 'DD/MM/YYYY HH:mm:ss',
                    locale: 'es',
                    dateFormatExport: date => {
                        return `${date
                            .getDate()
                            .toString()
                            .padStart(2, '0')}/${(date.getMonth() + 1)
                                .toString()
                                .padStart(2, '0')}/${date.getFullYear()}, ${date
                                    .getHours()
                                    .toString()
                                    .padStart(2, '0')}:${date
                                        .getMinutes()
                                        .toString()
                                        .padStart(2, '0')}:${date
                                            .getSeconds()
                                            .toString()
                                            .padStart(2, '0')}`;
                    },
                };
            case 'bg':
            default:
                return {
                    format: 'DD.MM.YYYY г., HH:mm:ss ч.',
                    locale: 'bg',
                    dateFormatExport: date => {
                        return `${date
                            .getDate()
                            .toString()
                            .padStart(2, '0')}.${(date.getMonth() + 1)
                                .toString()
                                .padStart(2, '0')}.${date.getFullYear()} г., ${date
                                    .getHours()
                                    .toString()
                                    .padStart(2, '0')}:${date
                                        .getMinutes()
                                        .toString()
                                        .padStart(2, '0')}:${date
                                            .getSeconds()
                                            .toString()
                                            .padStart(2, '0')} ч.`;
                    },
                };
        }
    };

    // Get the current date format configuration
    const [dateConfig, setDateConfig] = useState(getDateFormatAndLocale());

    // Update date config when language changes
    useEffect(() => {
        setDateConfig(getDateFormatAndLocale());
    }, [i18n.language]);

    // ! Permissions Check
    const isSuper = useCheckPermission('super');
    const canRead = useCheckPermission('r');
    const canCreate = useCheckPermission('c');
    const canUpdate = useCheckPermission('u');
    const canDelete = useCheckPermission('d');

    // Get user role from localStorage as a simple string
    const [isAnalyzer, setIsAnalyzer] = useState(false);

    // Initialize user role from localStorage when component mounts
    useEffect(() => {
        const userRole = localStorage.getItem('level');
        // Check if the role is 'analyzer'
        if (userRole) {
            setIsAnalyzer(userRole === 'analyzer');
        }
    }, []);

    const classes = useStyles();
    const [events, setEvent] = useState([]);
    const [selectedStartDate, handleStartDateChange] = useState(null);
    const [selectedEndDate, handleEndDateChange] = useState(
        getDefaultToolbarEndDate
    );

    const [loc] = React.useState(() => {
        const locData = localStorage.getItem('loc');
        return locData ? JSON.parse(locData).id : '';
    });

    const departmentsOptions = useDepartmentsOptions();
    const [isLoading, setIsLoading] = useState(false);

    const applyEventsData = useCallback(
        data => {
            if (!isMountedRef.current) {
                return;
            }
            setEvent(sanitizeEventRows(data));
        },
        [isMountedRef]
    );

    const retrieveEvents = useCallback(async () => {
        const fetchId = ++fetchIdRef.current;
        setIsLoading(true);
        const locData = JSON.parse(localStorage.getItem('loc'));

        try {
            let response;

            if (Array.isArray(locData)) {
                const joinedValues = locData.join(',');
                response = await EventDataService.getAll(joinedValues);
            } else if (locData && locData.id === 'all') {
                response = await EventDataService.adminAll();
            }

            if (fetchId !== fetchIdRef.current) {
                return;
            }

            if (response && response.data) {
                applyEventsData(response.data);
            }
        } catch (e) {
            console.error(e);
        } finally {
            if (fetchId === fetchIdRef.current && isMountedRef.current) {
                setIsLoading(false);
            }
        }
    }, [applyEventsData, isMountedRef]);

    const retrieveFilteredEvents = useCallback(async () => {
        if (!selectedStartDate || !selectedEndDate) {
            return;
        }

        const fetchId = ++fetchIdRef.current;
        setIsLoading(true);

        const locData = JSON.parse(localStorage.getItem('loc'));
        dayjs.locale(dateConfig.locale);

        const selectedStartDateFormatted =
            formatToolbarDateForApi(selectedStartDate);
        const selectedEndDateFormatted =
            formatToolbarDateForApi(selectedEndDate);

        try {
            let response;

            if (Array.isArray(locData)) {
                const joinedValues = locData.join(',');
                response = await EventDataService.getAllDateFilter(
                    joinedValues,
                    selectedStartDateFormatted,
                    selectedEndDateFormatted
                );
            } else if (locData && locData.id === 'all') {
                response = await EventDataService.adminAllDateFilter(
                    selectedStartDateFormatted,
                    selectedEndDateFormatted
                );
            }

            if (fetchId !== fetchIdRef.current) {
                return;
            }

            if (response && response.data) {
                applyEventsData(response.data);
            }
        } catch (e) {
            console.error(e);
        } finally {
            if (fetchId === fetchIdRef.current && isMountedRef.current) {
                setIsLoading(false);
            }
        }
    }, [
        applyEventsData,
        dateConfig.locale,
        isMountedRef,
        selectedEndDate,
        selectedStartDate,
    ]);

    const refreshTableData = useCallback(async () => {
        if (selectedStartDate && selectedEndDate) {
            await retrieveFilteredEvents();
        } else {
            await retrieveEvents();
        }
    }, [
        retrieveEvents,
        retrieveFilteredEvents,
        selectedEndDate,
        selectedStartDate,
    ]);

    const handleEventCreated = useCallback(
        newRow => {
            const row = sanitizeEventRow(newRow);
            if (!row || !row.id) {
                return { added: false };
            }

            if (
                !isEventInDateRange(
                    row.time,
                    selectedStartDate,
                    selectedEndDate
                )
            ) {
                return { added: false, outsideFilter: true };
            }

            setEvent(prev => {
                const idx = prev.findIndex(e => e.id === row.id);
                if (idx >= 0) {
                    return prev.map((e, i) =>
                        i === idx ? { ...e, ...row } : e
                    );
                }
                return [row, ...prev];
            });

            return { added: true };
        },
        [selectedEndDate, selectedStartDate]
    );

    useEffect(() => {
        retrieveLocations();
        retrieveEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [locationOptions, setLocationOptions] = useState([]);

    const retrieveLocations = () => {
        LocationDataService.getAll()
            .then(response => {
                const options = response.data.reduce((acc, item) => {
                    acc[item.name] = item.name;
                    return acc;
                }, {});
                setLocationOptions(options);
            })
            .catch(e => {
                setErrorMessages(['Cannot load user data']);
                setIserror(true);
            });
    };

    //for error handling
    const [iserror, setIserror] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);

    const handleEventRemove = useCallback((oldData, resolve) => {
        const adminId = getAdminIdFromCookie();
        if (!adminId) {
            setErrorMessages(['Delete failed! Server error']);
            setIserror(true);
            resolve();
            return;
        }

        EventDataService.remove(oldData.id, { adminId })
            .then(res => {
                // Update the state with the filtered events
                setEvent(prevEvents =>
                    prevEvents.filter(event => event.id !== oldData.id)
                );
                resolve();
                // Optional: Add success message or notification here
            })
            .catch(error => {
                console.error('Delete failed:', error);
                setErrorMessages(['Delete failed! Server error']);
                setIserror(true);
                resolve();
            });
    }, []);

    //! Edit User Event Start
    const [submitted, setSubmitted] = useState(false);

    const handleEventUpdate = useCallback(async (newData, resolve) => {
        try {
            const adminId = getAdminIdFromCookie();
            if (!adminId) {
                throw new Error('Missing admin session');
            }

            const dataToSend = { ...newData };
            delete dataToSend.tableData;
            dataToSend.genuine = false;
            dataToSend.adminId = adminId;

            const response = await EventDataService.update(
                dataToSend.id,
                dataToSend
            );

            if (response && response.data) {
                const updatedEvent = response.data;

                // Update the state with the updated event
                setEvent(prevEvents =>
                    prevEvents.map(event =>
                        event.id === updatedEvent.id
                            ? sanitizeEventRow({
                                  ...event,
                                  ...updatedEvent,
                              })
                            : event
                    )
                );
            }

            resolve();
        } catch (e) {
            console.error('Error in handleEventUpdate:', e);
            setErrorMessages(['Update failed! Server error']);
            setIserror(true);
            resolve();
        }
    }, []);

    //! Edit User Event Stop

    const [open, setOpen] = React.useState(false);
    const handleClickOpen = () => {
        setOpen(true);
    };

    // var loc_name = events.reduce(function (acc, cur, i) {
    //     acc[cur.id] = cur.userEvent.name;

    //     return acc;
    // }, {});

    // var sub_loc_name = departments.reduce(function (acc, sub_loc, i) {
    //     acc[sub_loc.id] = sub_loc.name;

    //     return acc;
    // }, {});

    const SPACED_DATE_FORMAT = 'DD/MMM/YYYY H:MM:ss';

    // * Add Filters to State Start
    const [selectedRows, setSelectedRows] = useState([]);

    const tableComponents = useMemo(
        () => ({
            Toolbar: props => (
                <div>
                    <MTableToolbar {...props} />
                </div>
            ),
            FilterRow: props => <MTableFilterRow {...props} />,
        }),
        []
    );

    const columns = useMemo(
        () => [
            {
                title: t('reports.eventsReport.date'),
                field: 'time',
                type: 'datetime',
                dateSetting: {
                    locale: dateConfig.locale,
                    format: dateConfig.format,
                    hour12: false,
                },
                filtering: false,
                emptyValue: (e, rowData) => {
                    return 'empty';
                },
                render: rowData => {
                    if (
                        rowData.time &&
                        rowData.time !== 'null' &&
                        rowData.time !== 'empty'
                    ) {
                        if (i18n.language === 'bg') {
                            const date = toTzDate(rowData.time);
                            return `${date
                                .getDate()
                                .toString()
                                .padStart(2, '0')}.${(date.getMonth() + 1)
                                .toString()
                                .padStart(
                                    2,
                                    '0'
                                )}.${date.getFullYear()} г., ${date
                                .getHours()
                                .toString()
                                .padStart(2, '0')}:${date
                                .getMinutes()
                                .toString()
                                .padStart(2, '0')}:${date
                                .getSeconds()
                                .toString()
                                .padStart(2, '0')} ч.`;
                        }
                        return dayjs(rowData.time)
                            .tz(getAppTimezone())
                            .locale(dateConfig.locale)
                            .format(dateConfig.format);
                    }
                    return '';
                },
                editComponent: props => (
                    <EventDateTimeEditField
                        value={props.value}
                        onChange={props.onChange}
                        dateConfig={dateConfig}
                        t={t}
                    />
                ),
            },
            {
                title: t('reports.eventsReport.employeeID'),
                field: 'sap',
                editable: 'never',
                emptyValue: (e, rowData) => {
                    return 'empty';
                },
            },
            {
                title: t('reports.eventsReport.employeeName'),
                field: 'employee_name',
                editable: 'never',
                emptyValue: (e, rowData) => {
                    return 'empty';
                },
            },
            {
                title: t('reports.eventsReport.deviceLocation'),
                field: 'location_name',
                editable: 'never',
                emptyValue: (e, rowData) => {
                    return 'empty';
                },
            },
            {
                title: t('reports.eventsReport.genuine'),
                field: 'genuine',
                filtering: false,
                render: rowData =>
                    rowData.genuine
                        ? t('reports.eventsReport.genuineValueFalse')
                        : t('reports.eventsReport.genuineValue'),
                emptyValue: (e, rowData) => {
                    return 'empty';
                },
                editable: 'never',
            },
            {
                title: t('reports.eventsReport.event'),
                field: 'event',
                lookup: {
                    enter: 'enter',
                    exit: 'exit',
                    access: 'access',
                    antipassback: 'antipassback',
                },
                emptyValue: (e, rowData) => {
                    return 'empty';
                },
            },

            {
                title: t('reports.eventsReport.deviceName'),
                field: 'device_name',
                editable: 'never',
                emptyValue: (e, rowData) => {
                    return 'empty';
                },
            },

            {
                title: t('reports.eventsReport.department'),
                field: 'sub_loc_name',
                lookup: departmentsOptions,
                editable: 'never',
                emptyValue: (e, rowData) => {
                    return 'empty';
                },
            },
        ],
        [t, i18n.language, dateConfig, departmentsOptions]
    );

    // ! Format the Date and time columns for export
    const formatDate = inputTime => {
        if (inputTime && inputTime !== 'null' && inputTime !== 'empty') {
            const date = toTzDate(inputTime);
            return dateConfig.dateFormatExport(date);
        }
        return 'empty';
    };

    // ! Header mapping for exported column titles
    const headerMapping = {};
    columns.forEach(col => {
        headerMapping[col.field] = col.title;
    });

    const mapHeaders = row => {
        let newRow = {};
        for (let key in row) {
            if (headerMapping[key]) {
                newRow[headerMapping[key]] = row[key];
            } else {
                newRow[key] = row[key];
            }
        }
        return newRow;
    };

    // ! Format the name for the file
    const formatDateForFilename = inputDate => {
        const date = new Date(inputDate);
        return `${date.getFullYear()}-${(date.getMonth() + 1)
            .toString()
            .padStart(2, '0')}-${date
                .getDate()
                .toString()
                .padStart(2, '0')}`;
    };

    const formattedStartDate = formatDateForFilename(selectedStartDate);
    const formattedEndDate = formatDateForFilename(selectedEndDate);

    // ! Export to XLS Function
    const exportTable = () => {
        const currentTableData = getMaterialTableExportData(tableRef);

        const transformedData = currentTableData.map(row => {
            // Check for << and >> conditions for first_time and last_time
            const firstTimeValue =
                row.first_time === '<<'
                    ? 'липсва вход'
                    : formatDate(row.first_time);
            const lastTimeValue =
                row.last_time === '>>'
                    ? 'липсва изход'
                    : formatDate(row.last_time);

            const genuine = row.genuine
                ? t('reports.eventsReport.genuineValueFalse')
                : t('reports.eventsReport.genuineValue');

            return {
                ...row,
                time: formatDate(row.time),
                first_time: firstTimeValue,
                last_time: lastTimeValue,
                genuine: genuine,
            };
        });

        // Only include displayed columns in the export
        const exportedData = transformedData.map(row => {
            let newRow = {};
            columns.forEach(col => {
                newRow[col.field] = row[col.field];
            });
            return mapHeaders(newRow);
        });

        // Export the data
        const ws = XLSX.utils.json_to_sheet(exportedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

        const buf = XLSX.write(wb, { bookType: 'xls', type: 'array' });
        saveAs(
            new Blob([buf], { type: 'application/vnd.ms-excel' }),
            `Events.xls`
        );
    };

    // ! Export to CSV Function
    const exportTableCsv = () => {
        const currentTableData = getMaterialTableExportData(tableRef);

        const transformedData = currentTableData.map(row => {
            const firstTimeValue =
                row.first_time === '<<'
                    ? 'липсва вход'
                    : formatDate(row.first_time);
            const lastTimeValue =
                row.last_time === '>>'
                    ? 'липсва изход'
                    : formatDate(row.last_time);

            const genuine = row.genuine
                ? t('reports.eventsReport.genuineValueFalse')
                : t('reports.eventsReport.genuineValue');

            return {
                ...row,
                time: formatDate(row.time),
                first_time: firstTimeValue,
                last_time: lastTimeValue,
                genuine: genuine,
            };
        });

        const exportedData = transformedData.map(row => {
            let newRow = {};
            columns.forEach(col => {
                newRow[col.field] = row[col.field];
            });
            return mapHeaders(newRow);
        });

        const ws = XLSX.utils.json_to_sheet(exportedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

        const buf = XLSX.write(wb, { bookType: 'csv', type: 'array' });
        saveAs(
            new Blob([buf], { type: 'application/vnd.ms-excel' }),
            `events_export.csv`
        );
    };

    // ! Debouncing Update Button
    const [isUpdating, setIsUpdating] = useState(false);

    return (
        <div>
            <Paper>
                <Grid
                    container
                    spacing={1}
                    className={classes.absoluteContainer}
                    style={{ paddingLeft: '16px' }}
                >
                    <Grid item xs={4} lg={3} style={{ paddingTop: '20px' }}>
                        <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                            adapterLocale={dateConfig.locale}
                        >
                            <MobileDatePicker
                                closeOnSelect={true}
                                id="selectedStartDate"
                                label={t('reports.common.startDate')}
                                ampm={false}
                                value={selectedStartDate}
                                onChange={date =>
                                    handleStartDateChange(
                                        normalizeToolbarStartDate(date)
                                    )
                                }
                                slotProps={reportDatePickerSlotProps}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={4} lg={3} style={{ paddingTop: '20px' }}>
                        <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                            adapterLocale={dateConfig.locale}
                        >
                            <MobileDatePicker
                                closeOnSelect={true}
                                id="selectedEndDate"
                                label={t('reports.common.endDate')}
                                value={selectedEndDate}
                                onChange={date =>
                                    handleEndDateChange(
                                        normalizeToolbarEndDate(date)
                                    )
                                }
                                slotProps={reportDatePickerSlotProps}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={4} lg={6}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            className={classes.filterButton}
                            onClick={retrieveFilteredEvents}
                        >
                            {t('reports.common.filter')}
                        </Button>
                    </Grid>
                </Grid>
                {canCreate && (
                    <ModalForm
                        buttonLabel={t('reports.common.add')}
                        onEventCreated={handleEventCreated}
                    />
                )}
                <MaterialTable
                title={t('reports.eventsReport.title')}
                columns={columns}
                data={events}
                isLoading={isLoading}
                tableRef={tableRef}
                style={{
                    borderRadius: '0',
                    boxShadow: 'none',
                }}
                actions={[
                    {
                        icon: 'refresh',
                        tooltip: 'Refresh',
                        isFreeAction: true,
                        onClick: () => refreshTableData(),
                    },
                    ...(!isAnalyzer
                        ? [
                            {
                                icon: () => <DownloadOutlinedIcon />,
                                tooltip: 'Export to CSV',
                                isFreeAction: true,
                                onClick: exportTableCsv,
                            },
                            {
                                icon: () => <DescriptionIcon />,
                                tooltip: 'Export to Excel',
                                isFreeAction: true,
                                onClick: exportTable,
                            },
                        ]
                        : []),
                ]}
                options={{
                    loadingType: 'linear',
                    // rowStyle: {
                    //     overflowWrap: 'break-word'
                    // },
                    pageSize: 100,
                    maxBodyHeight: '68vh',
                    showTitle: false,
                    sorting: true,
                    actionsColumnIndex: -1,
                    // Conditionally enable/disable the export button based on the user role
                    exportButton: false,
                    filtering: true,
                    pageSizeOptions: [
                        20,
                        50,
                        100,
                        200,
                        300,
                        400,
                        500,
                        5000,
                        10000,
                    ],
                    search: true,
                    toolbar: true,
                }}
                components={tableComponents}
                editable={{
                    ...(canUpdate
                        ? {
                            onRowUpdate: (newData, oldData) =>
                                new Promise(resolve => {
                                    handleEventUpdate(newData, resolve);
                                }),
                        }
                        : {}),
                    ...(canDelete
                        ? {
                            onRowDelete: oldData =>
                                new Promise(resolve => {
                                    handleEventRemove(oldData, resolve);
                                }),
                        }
                        : {}),
                }}
                // onSelectionChange={rows => {
                //     setSelectedRows(rows)
                // }}
                localization={{
                    pagination: {
                        labelDisplayedRows:
                            '{from}-{to} ' +
                            t('reports.common.of') +
                            ' {count}',
                        labelRowsPerPage: t('reports.common.rowsPerPage'),
                        labelRowsSelect: t('reports.common.rowsSelect'),
                    },
                    header: {
                        actions: t('reports.common.actions'),
                    },
                    body: {
                        emptyDataSourceMessage: t(
                            'reports.common.emptyDataSourceMessage'
                        ),
                        editTooltip: t('reports.common.edit'),
                        deleteTooltip: t('reports.common.delete'),
                        filterRow: {
                            filterTooltip: t('reports.common.filter'),
                        },
                        editRow: {
                            deleteText: t('reports.common.deleteConfirmation'),
                            cancelTooltip: t('reports.common.cancel'),
                            saveTooltip: t('reports.common.save'),
                        },
                    },
                    toolbar: {
                        exportTitle: t('reports.common.export'),
                        exportName: t('reports.common.exportCSV'),
                        searchTooltip: t('reports.common.search'),
                        searchPlaceholder: t(
                            'reports.common.searchPlaceholder'
                        ),
                        nRowsSelected: t('reports.common.rowsSelected', {
                            count: selectedRows.length,
                        }),
                    },
                }}
            />
            </Paper>
        </div>
    );
}

export default EventsTable;
